/**
 * Lớp gọi API duy nhất của frontend.
 *
 * Mọi màn hình đi qua đây, không fetch thẳng — để token, lỗi 401 và định dạng
 * lỗi được xử lý ở một chỗ.
 */

const KHOA_TOKEN = 'alads.token';

/**
 * Token giữ trong biến của module, `localStorage` chỉ là nơi lưu thêm để còn
 * đăng nhập sau khi tải lại trang.
 *
 * Phải có bộ báo tin `nguoiNghe`: nếu để React đọc thẳng `localStorage` trong
 * lúc render thì đổi token xong giao diện không biết gì. Lỗi đã gặp thật — đăng
 * nhập thành công ở server nhưng màn hình đứng nguyên ở trang đăng nhập.
 */
let tokenHienTai: string | null = (() => {
  try {
    return localStorage.getItem(KHOA_TOKEN);
  } catch {
    return null;
  }
})();

const nguoiNghe = new Set<() => void>();

export function docToken(): string | null {
  return tokenHienTai;
}

export function luuToken(token: string | null): void {
  if (tokenHienTai === token) return;
  tokenHienTai = token;
  try {
    if (token) localStorage.setItem(KHOA_TOKEN, token);
    else localStorage.removeItem(KHOA_TOKEN);
  } catch {
    /* trình duyệt chặn localStorage — phiên chỉ sống trong tab này */
  }
  for (const bao of nguoiNghe) bao();
}

/** Đăng ký nhận tin khi token đổi. Trả về hàm huỷ đăng ký. */
export function theoDoiToken(bao: () => void): () => void {
  nguoiNghe.add(bao);
  return () => nguoiNghe.delete(bao);
}

/**
 * Công ty đang chọn (BOSS 29/09/2026 — nhiều công ty trong một ALADS). Gửi kèm
 * mọi request qua header `X-Cong-Ty`; máy chủ lọc dữ liệu và tính quyền theo
 * vai trò TẠI công ty này. null = để máy chủ tự chọn mặc định.
 */
const KHOA_CONG_TY = 'alads.cong-ty';
let congTyHienTai: string | null = (() => {
  try {
    return localStorage.getItem(KHOA_CONG_TY);
  } catch {
    return null;
  }
})();
const ngheCongTy = new Set<() => void>();

export function docCongTy(): string | null {
  return congTyHienTai;
}

export function luuCongTy(giaTri: string | null): void {
  if (congTyHienTai === giaTri) return;
  congTyHienTai = giaTri;
  try {
    if (giaTri) localStorage.setItem(KHOA_CONG_TY, giaTri);
    else localStorage.removeItem(KHOA_CONG_TY);
  } catch {
    /* trình duyệt chặn lưu — chỉ sống trong tab này */
  }
  for (const bao of ngheCongTy) bao();
}

export function theoDoiCongTy(bao: () => void): () => void {
  ngheCongTy.add(bao);
  return () => ngheCongTy.delete(bao);
}

/**
 * Token đánh dấu máy này đã xác thực 2 lớp một lần.
 *
 * Gửi kèm ở bước 1 của đăng nhập; backend thấy hợp lệ thì bỏ qua bước nhập mã
 * 6 số. Đây KHÔNG phải token đăng nhập — tự nó không mở được API nào, chỉ nói
 * "máy này đã từng qua bước mã rồi".
 */
const KHOA_THIET_BI = 'alads.thiet-bi';

export function docTokenThietBi(): string | null {
  try {
    return localStorage.getItem(KHOA_THIET_BI);
  } catch {
    return null;
  }
}

export function luuTokenThietBi(token: string | null): void {
  try {
    if (token) localStorage.setItem(KHOA_THIET_BI, token);
    else localStorage.removeItem(KHOA_THIET_BI);
  } catch {
    /* trình duyệt chặn localStorage — lần sau sẽ hỏi lại mã, không sao */
  }
}

export class LoiApi extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly than?: unknown,
  ) {
    super(message);
    this.name = 'LoiApi';
  }
}

export interface TuyChonGoi {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  than?: unknown;
  signal?: AbortSignal;
}

/**
 * Câu báo khi không với tới được máy chủ.
 *
 * Tách riêng vì đây là lỗi người dùng gặp nhiều nhất lúc dev, và câu chữ phải
 * nói đúng việc cần làm. Trước đây chỗ này rơi vào câu dự phòng "Lỗi 500 khi
 * gọi /auth/dang-nhap" — đúng về kỹ thuật nhưng không giúp gì: người đọc không
 * thể biết rằng chỉ cần bật lại backend.
 */
const KHONG_KET_NOI_DUOC =
  'Không kết nối được máy chủ ALADS. Backend chưa chạy hoặc vừa dừng — chạy CHAY-BACKEND.cmd trong thư mục alads-app rồi thử lại.';

export async function goiApi<T>(duong: string, tuyChon: TuyChonGoi = {}): Promise<T> {
  const token = docToken();

  let phanHoi: Response;
  try {
    phanHoi = await fetch(`/api${duong}`, {
      method: tuyChon.method ?? 'GET',
      headers: {
        ...(tuyChon.than ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(congTyHienTai ? { 'X-Cong-Ty': congTyHienTai } : {}),
      },
      body: tuyChon.than ? JSON.stringify(tuyChon.than) : undefined,
      signal: tuyChon.signal,
    });
  } catch (loi) {
    // Người dùng tự bấm huỷ thì giữ nguyên lỗi, đừng báo thành mất kết nối.
    if (loi instanceof DOMException && loi.name === 'AbortError') throw loi;
    // `fetch` chỉ ném khi không gửi đi được: mất mạng, sai địa chỉ, hoặc cả
    // Vite lẫn backend đều tắt.
    throw new LoiApi(0, KHONG_KET_NOI_DUOC, null);
  }

  let than: unknown = null;
  const kieu = phanHoi.headers.get('content-type') ?? '';
  if (kieu.includes('application/json')) {
    than = await phanHoi.json().catch(() => null);
  }

  if (!phanHoi.ok) {
    if (phanHoi.status === 401) luuToken(null);

    const loiCuaMayChu = (than as { loi?: string } | null)?.loi;
    /**
     * 502/503/504 nghĩa là lớp đứng trước (proxy Vite lúc dev, Nginx trên VPS)
     * không với tới được backend. Body khi đó thường là trang HTML của chính
     * lớp đó chứ không phải JSON của ALADS, nên phải tự đặt câu cho đúng việc.
     */
    const mayChuChetDung = phanHoi.status === 502 || phanHoi.status === 503 || phanHoi.status === 504;
    const thongBao =
      loiCuaMayChu ??
      (mayChuChetDung ? KHONG_KET_NOI_DUOC : `Lỗi ${phanHoi.status} khi gọi ${duong}`);

    throw new LoiApi(phanHoi.status, thongBao, than);
  }

  return than as T;
}

/** Định dạng tiền theo đúng cách thiết kế gốc hiển thị: `18.420.000₫`. */
export function vnd(n: number | null | undefined): string {
  return `${(n ?? 0).toLocaleString('vi-VN')}₫`;
}
